import { BrandReviewStatusIcon } from '@/components/BrandChip';
import { HintTooltip } from '@/components/HintTooltip';
import { Badge } from '@/components/ui/badge';
import type { Brand, BrandId, Model } from '@rock/types';
import type { FC, ReactNode } from 'react';
import { type ModelBrandState, modelBrandLabel, modelBrandState } from './modelBrand';
import { PROVISIONAL_HINT } from './ModelMatchingPanels';

/** `linked` gets no badge: most rows are linked, and one on every row would hide the few that need a human. */
const BRAND_STATE_BADGE: Partial<Record<ModelBrandState, { label: string; className: string; hint: ReactNode }>> = {
    provisional: {
        label: 'Provisional',
        className: 'bg-sky-100 text-sky-900 border-transparent',
        hint: PROVISIONAL_HINT,
    },
    suggested: {
        label: 'Suggested',
        className: 'bg-amber-100 text-amber-900 border-transparent',
        hint: 'A brand research proposed, waiting for a human to confirm or dismiss it on the model page.',
    },
    unlinked: {
        label: 'Unlinked',
        className: 'bg-slate-200 text-slate-700 border-transparent',
        hint: 'Only the brand as research or the owner wrote it. No catalog brand is linked, so nothing keys on this name.',
    },
};

/**
 * The Brand cell: the name plus a badge saying how much it's worth. A pending suggestion shows the PROPOSED
 * name rather than `modelBrandLabel`'s enrichment fallback — they're usually the same string, but the
 * suggestion is the thing the admin is being asked to act on.
 */
export const ModelBrandCell: FC<{ model: Model; brandLookup: Map<BrandId, Brand> }> = ({ model, brandLookup }) => {
    const state = modelBrandState(model);
    const badge = BRAND_STATE_BADGE[state];
    const linked = model.brandId ? brandLookup.get(model.brandId) : undefined;
    const label =
        (state === 'suggested' || state === 'provisional') && model.brandSuggestion
            ? model.brandSuggestion.name
            : (modelBrandLabel(model, brandLookup) ?? '—');
    return (
        <span className="flex flex-wrap items-center gap-1.5">
            {linked && <BrandReviewStatusIcon brand={linked} />}
            {label}
            {badge && (
                <HintTooltip hint={badge.hint}>
                    <Badge variant="secondary" className={badge.className}>
                        {badge.label}
                    </Badge>
                </HintTooltip>
            )}
        </span>
    );
};
